import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PublicReceiptResult, ReceiptPublicService } from './receipt-public.service';

export type ReceiptStep = 'loading' | 'ready' | 'error';

/**
 * Fixed, customer-safe error copy. Raw backend error messages are never
 * rendered — only these strings reach the template.
 */
export const RECEIPT_ERROR_INVALID = 'This receipt link is invalid or has expired.';
export const RECEIPT_ERROR_EXPIRED = 'This receipt link has expired.';
export const RECEIPT_ERROR_GENERIC = 'Something went wrong. Please try again later.';

/**
 * Customer-facing label from a backend wash_type code: 'single_wash' → 'Single wash'.
 * Values without underscores pass through unchanged.
 */
export function humanizeWashType(washType: string | null): string | null {
  if (!washType) {
    return washType;
  }
  const words = washType.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Public shop-branded receipt page opened from post-payment SMS links.
 *
 * SMS URL shape:
 *   /receipt?token={64-hex receipt token}
 */
@Component({
  selector: 'app-receipt-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './receipt-page.component.html',
  styleUrl: './receipt-page.component.scss',
})
export class ReceiptPageComponent implements OnInit, OnDestroy {
  readonly step = signal<ReceiptStep>('loading');
  readonly errorMessage = signal('');
  readonly receipt = signal<PublicReceiptResult | null>(null);
  /** Signed https logo URL; cleared when the image fails to load. */
  readonly logoUrl = signal('');

  private routeSub?: Subscription;
  private loadSub?: Subscription;
  private lastToken: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly receiptPublic: ReceiptPublicService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe(query => {
      const token = query.get('token')?.trim() || '';
      if (token === this.lastToken) {
        return;
      }
      this.lastToken = token;
      if (!token) {
        this.showError(RECEIPT_ERROR_INVALID);
        return;
      }
      this.loadReceipt(token);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  onLogoError(): void {
    this.logoUrl.set('');
  }

  washTypeLabel(washType: string | null): string | null {
    return humanizeWashType(washType);
  }

  private loadReceipt(token: string): void {
    this.loadSub?.unsubscribe();
    this.step.set('loading');
    this.errorMessage.set('');
    this.loadSub = this.receiptPublic.loadReceipt(token).subscribe({
      next: response => {
        const data = response?.data;
        if (!data) {
          this.showError(RECEIPT_ERROR_GENERIC);
          return;
        }
        this.receipt.set(data);
        this.logoUrl.set(data.shopLogo || '');
        this.step.set('ready');
      },
      error: (error: unknown) => {
        this.handleLoadError(error);
      },
    });
  }

  private handleLoadError(error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 404) {
      this.showError(RECEIPT_ERROR_INVALID);
      return;
    }
    if (status === 410) {
      this.showError(RECEIPT_ERROR_EXPIRED);
      return;
    }
    this.showError(RECEIPT_ERROR_GENERIC);
  }

  private showError(message: string): void {
    this.receipt.set(null);
    this.logoUrl.set('');
    this.step.set('error');
    this.errorMessage.set(message);
  }
}
